import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { getAllPoems, updatePoem } from '@/services/poemService'; // Assuming updatePoem exists and can handle is_protected
import { submitProtectedPoem } from '@/services/adminService'; 
import type { Database } from '@/types/supabase';

// Define Poem type - Allow null ID
type Poem = {
    id: string | null; // Allow null ID
    created_at: string | null;
    title: string | null;
    content: string | null;
    poet_id: string | null;
    subject: string | null;
    is_generated: boolean | null;
    generated_prompt: string | null;
    ai_response?: any | null; 
    prompt_template_id: string | null;
    is_archived: boolean | null;
    archived_at?: string | null; 
    view_count: number | null;
    likes: number | null;
    poet_name: string | null; // Included from the view join
    highlights?: any | null; 
    system_prompt?: string | null;
    updated_at?: string | null;
    is_protected?: boolean; // Added based on schema change
};

const AdminProtectedPoems = () => {
    const { toast } = useToast();
    const [poems, setPoems] = useState<Poem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // State for the submission form
    const [newPoemTitle, setNewPoemTitle] = useState('');
    const [newPoemPoetName, setNewPoemPoetName] = useState('');
    const [newPoemContent, setNewPoemContent] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch poems on mount
    useEffect(() => {
        fetchPoems();
    }, []);

    const fetchPoems = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const fetchedPoems: Poem[] = await getAllPoems({ includeArchived: true }); 
            // Filter out poems with null IDs before sorting and setting state
            const validPoems = fetchedPoems.filter(poem => poem.id !== null);
            validPoems.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
            setPoems(validPoems);
        } catch (err) {
            console.error("Error fetching poems:", err);
            setError(err instanceof Error ? err.message : "Failed to load poems");
            toast({ title: "Error", description: "Could not fetch poems.", variant: "destructive" });
        } finally {
            setIsLoading(false);
        }
    };

    // Handler for toggling protected status
    const handleToggleProtected = async (poemId: string | null, currentStatus: boolean) => {
        if (!poemId) { 
             toast({title: "Error", description: "Cannot toggle status for poem with missing ID.", variant: "destructive"});
             return;
        }
        console.log(`Toggling protected status for poem ${poemId} to ${!currentStatus}`);
        
        // Store original status for potential revert
        const originalStatus = currentStatus;
        const poemIndex = poems.findIndex(p => p.id === poemId);
        if (poemIndex === -1) return;

        // Optimistically update the UI state
        const updatedPoems = [...poems];
        const updatedPoem = { ...updatedPoems[poemIndex], is_protected: !currentStatus };
        updatedPoems[poemIndex] = updatedPoem;
        setPoems(updatedPoems);

        // Call the backend API via poemService.updatePoem
        try {
             // The updates object now includes is_protected
             await updatePoem(poemId, { is_protected: !currentStatus }); 
             toast({ title: "Success", description: `Poem ${poemId} protected status updated.` });
             // Optionally refetch poems to ensure consistency, though optimistic update handles UI
             // await fetchPoems();
        } catch (err) {
             console.error("Error updating protected status:", err);
             toast({ title: "Error", description: `Failed to update protected status: ${err instanceof Error ? err.message : 'Unknown error'}`, variant: "destructive" });
             // Revert optimistic update on error
             updatedPoems[poemIndex] = { ...updatedPoems[poemIndex], is_protected: originalStatus }; // Revert is_protected
             setPoems([...updatedPoems]); 
        }
    };

    // Handler for submitting a new protected poem
    const handleSubmitNewProtectedPoem = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!newPoemTitle || !newPoemPoetName || !newPoemContent) {
             toast({ title: "Missing Fields", description: "Please fill in title, poet name, and content.", variant: "default" });
             return;
        }

        const payload = { 
            title: newPoemTitle.trim(), 
            poetName: newPoemPoetName.trim(), // Pass poetName
            content: newPoemContent.trim() 
            // TODO: Add subject if needed/implemented
        };
        
        console.log("Submitting new protected poem:", payload);
        setIsSubmitting(true);

        try {
            // Call the actual service function
            const newProtectedPoem = await submitProtectedPoem(payload);
            
            toast({ title: "Success", description: `Poem "${newProtectedPoem.title}" submitted as protected.` });
            // Clear form
            setNewPoemTitle('');
            setNewPoemPoetName('');
            setNewPoemContent('');
            // Refetch poems list to include the new one
            await fetchPoems(); 

        } catch (err) {
            console.error("Error submitting protected poem:", err);
            toast({ title: "Error", description: `Failed to submit protected poem: ${err instanceof Error ? err.message : 'Unknown error'}`, variant: "destructive" });
        } finally {
             setIsSubmitting(false);
        }
    };


    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            <h1 className="text-2xl font-bold mb-6">Manage Protected Poems</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Column 1: List of Poems */}
                <Card>
                    <CardHeader>
                        <CardTitle>Existing Poems</CardTitle>
                        <CardDescription>Toggle the 'Protected' status for poems. Protected poems ignore the 3-poem limit.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isLoading && (
                            <div className="flex justify-center items-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        )}
                        {error && <p className="text-destructive text-center py-4">{error}</p>}
                        {!isLoading && !error && poems.length === 0 && (
                            <p className="text-muted-foreground text-center py-4">No poems found.</p>
                        )}
                        {!isLoading && !error && poems.length > 0 && (
                            <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                                {poems.map((poem) => (
                                    // Key requires a non-null string
                                    poem.id ? (
                                        <li key={poem.id} className="flex justify-between items-center border p-3 rounded shadow-sm">
                                            <div>
                                                <p className="font-medium">{poem.title || 'Untitled'}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {poem.poet_name || `ID: ${poem.id}`}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <Label htmlFor={`protected-toggle-${poem.id}`} className="text-sm">
                                                    Protected
                                                </Label>
                                                <Switch
                                                    id={`protected-toggle-${poem.id}`}
                                                    checked={!!poem.is_protected}
                                                    // Pass potentially null ID, handler checks it
                                                    onCheckedChange={() => handleToggleProtected(poem.id, !!poem.is_protected)}
                                                />
                                            </div>
                                        </li>
                                    ) : null // Don't render if ID is null
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                {/* Column 2: Submission Form */}
                <Card>
                    <CardHeader>
                        <CardTitle>Submit New Protected Poem</CardTitle>
                        <CardDescription>Add a new poem that will be protected by default and have highlights generated.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmitNewProtectedPoem} className="space-y-4">
                            <div>
                                <Label htmlFor="new-poem-title">Title</Label>
                                <Input 
                                    id="new-poem-title" 
                                    value={newPoemTitle} 
                                    onChange={(e) => setNewPoemTitle(e.target.value)}
                                    placeholder="Poem Title"
                                    required 
                                />
                            </div>
                            <div>
                                <Label htmlFor="new-poem-poet">Poet Name</Label>
                                <Input 
                                    id="new-poem-poet" 
                                    value={newPoemPoetName} 
                                    onChange={(e) => setNewPoemPoetName(e.target.value)}
                                    placeholder="Poet's Full Name"
                                    required 
                                />
                            </div>
                            <div>
                                <Label htmlFor="new-poem-content">Content</Label>
                                <Textarea 
                                    id="new-poem-content" 
                                    value={newPoemContent} 
                                    onChange={(e) => setNewPoemContent(e.target.value)}
                                    placeholder="Enter poem text here..."
                                    rows={10}
                                    required 
                                />
                            </div>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...
                                    </>
                                ) : (
                                    'Submit Protected Poem'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminProtectedPoems;
